import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import "./StaticPage.css";

const PrivacyPage: React.FC = () => {
    const navigate = useNavigate();

    return (
        
        <div className="static-page">
            <div className="static-page-inner">
                <button
                    className="back-link"
                    onClick={() =>
                        window.history.length > 1 ? navigate(-1) : navigate("/")
                    }
                >
                    ← Back
                </button>
                
                <h1>Privacy Policy</h1>
                <p className="static-page-meta">
                    Last updated: 1 December 2025
                </p>

                <p>
                    This Privacy Policy explains how <strong>Art Management Hub</strong>
                    ("we", "us", "our") collects, uses, and protects
                    personal information in connection with our private art and collection
                    management platform (the "Service").
                </p>

                <h2>1. Who this policy applies to</h2>
                <p>
                    This policy applies to users of the Service, including family office personnel,
                    principals, trusted advisors, and any other authorised users that access the
                    platform on behalf of our clients.
                </p>

                <h2>2. Information we collect</h2>
                <p>We may collect and process the following categories of information:</p>
                <ul>
                    <li>
                        <strong>Account and contact details</strong> – such as your name, email address,
                        role, and organisation.
                    </li>
                    <li>
                        <strong>Usage and technical information</strong> – such as log data, IP address,
                        browser type, device information, and pages accessed within the Service.
                    </li>
                    <li>
                        <strong>Collection-related information</strong> – information uploaded by you
                        or on your behalf relating to artworks and other assets, including images,
                        documents, valuations, and location details.
                    </li>
                </ul>

                <h2>3. How we use information</h2>
                <p>We use personal information for the following purposes:</p>
                <ul>
                    <li>To provide, maintain, and improve the Service.</li>
                    <li>To manage user accounts, authentication, and access permissions.</li>
                    <li>To respond to enquiries and support requests.</li>
                    <li>
                        To monitor and maintain the security and performance of the Service.
                    </li>
                    <li>
                        To meet legal, regulatory, and compliance obligations where applicable.
                    </li>
                </ul>

                <h2>4. Legal bases for processing</h2>
                <p>
                    Where required by applicable data protection laws, we rely on one or more of the
                    following legal bases to process personal data:
                </p>
                <ul>
                    <li>Performance of a contract with our clients.</li>
                    <li>Legitimate interests, such as securing and operating the Service.</li>
                    <li>Compliance with legal obligations.</li>
                    <li>Consent, where explicitly requested and provided.</li>
                </ul>

                <h2>5. Data sharing and transfers</h2>
                <p>
                    We do not sell personal data. We may share information with carefully selected
                    service providers that support the operation, hosting, and security of the Service.
                    These providers act on our instructions and are bound by appropriate confidentiality
                    and data protection obligations.
                </p>
                <p>
                    Where data is transferred outside of your home jurisdiction, we will take reasonable
                    steps to ensure that appropriate safeguards are in place, in line with applicable
                    data protection requirements.
                </p>

                <h2>6. Data security</h2>
                <p>
                    We implement technical and organisational measures designed to protect personal
                    information against unauthorised access, loss, misuse, or alteration. No system
                    can be completely secure, but we take security and confidentiality seriously and
                    review our controls on an ongoing basis.
                </p>

                <h2>7. Data retention</h2>
                <p>
                    We retain personal information for as long as necessary to provide the Service,
                    comply with legal obligations, resolve disputes, and enforce our agreements.
                    Retention periods may vary depending on the type of data and the context in
                    which it was collected.
                </p>

                <h2>8. Your rights</h2>
                <p>
                    Depending on your jurisdiction, you may have rights in relation to your personal
                    data, including the right to:
                </p>
                <ul>
                    <li>Request access to the personal data we hold about you.</li>
                    <li>Request correction or updating of inaccurate information.</li>
                    <li>Request deletion of personal data, subject to legal obligations.</li>
                    <li>Object to or restrict certain types of processing.</li>
                    <li>Request data portability, where applicable.</li>
                </ul>
                <p>
                    To exercise any of these rights, please contact us using the details below.
                    We may need to verify your identity before responding.
                </p>

                <h2>9. Client responsibilities</h2>
                <p>
                    In many cases, we act as a data processor on behalf of our clients (such as
                    family offices or related entities). Our clients are responsible for ensuring
                    that any personal data submitted to the Service is collected and used in line
                    with applicable laws and any notices they provide to individuals.
                </p>

                <h2>10. Contact</h2>
                <p>
                    If you have any questions about this Privacy Policy or how we handle personal
                    information, please contact:
                </p>
                <p className="static-page-highlight">
                    Email: <a href="mailto:dpo@artmanagementhub.com">dpo@artmanagementhub.com</a>
                </p>

                <h2>11. Changes to this policy</h2>
                <p>
                    We may update this Privacy Policy from time to time. The updated version will
                    be indicated by an updated "Last updated" date and will take effect
                    when made available through the Service.
                </p>
            </div>
        </div>
    );
};

export default PrivacyPage;
